import { X } from "lucide-react"
import { Toaster as Sonner, type ToasterProps } from "sonner"

const CIRCUMFERENCE = 100
const RADIUS = 16

function SuccessRing() {
  return (
    <svg viewBox="0 0 38 38" className="toast-success-ring" style={{ width: 34, height: 34 }}>
      <circle
        cx="19"
        cy="19"
        r={RADIUS}
        fill="none"
        stroke="currentColor"
        strokeWidth="3"
        opacity="0.25"
      />
      <circle
        cx="19"
        cy="19"
        r={RADIUS}
        fill="none"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
        strokeDasharray={CIRCUMFERENCE}
        strokeDashoffset="0"
        className="toast-success-ring-progress"
      />
      <polyline
        points="14,21 17.5,24.5 24,15"
        fill="none"
        stroke="var(--color-toast-success-text)"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function ErrorRing() {
  return (
    <svg viewBox="0 0 38 38" className="toast-error-ring" style={{ width: 34, height: 34 }}>
      <circle
        cx="19"
        cy="19"
        r={RADIUS}
        fill="none"
        stroke="currentColor"
        strokeWidth="3"
        opacity="0.25"
      />
      <circle
        cx="19"
        cy="19"
        r={RADIUS}
        fill="none"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
        strokeDasharray={CIRCUMFERENCE}
        strokeDashoffset="0"
        className="toast-error-ring-progress"
      />
      <path
        d="M5.35311 0.773922C5.92393 -0.257962 7.40737 -0.257975 7.97821 0.77389L13.1421 10.1082C13.6951 11.108 12.972 12.3343 11.8295 12.3343H1.50215C0.359633 12.3343 -0.36345 11.108 0.189589 10.1082L5.35311 0.773922ZM7.33155 9.66857C7.33155 9.30087 7.03347 9.00279 6.66578 9.00279C6.29808 9.00279 6 9.30087 6 9.66857C6 10.0363 6.29808 10.3343 6.66578 10.3343C7.03347 10.3343 7.33155 10.0363 7.33155 9.66857ZM7.15876 4.43306C7.12545 4.18904 6.91611 4.0011 6.66297 4.0013C6.38683 4.00152 6.16315 4.22556 6.16337 4.5017L6.16578 7.50277L6.17039 7.57061C6.2037 7.81463 6.41304 8.00257 6.66617 8.00236C6.94232 8.00214 7.166 7.77811 7.16578 7.50197L7.16337 4.5009L7.15876 4.43306Z"
        fill="var(--color-toast-error-text)"
        transform="translate(11.5, 10) scale(1.2)"
      />
    </svg>
  )
}

function WarningRing() {
  return (
    <svg viewBox="0 0 38 38" className="toast-warning-ring" style={{ width: 34, height: 34 }}>
      <circle
        cx="19"
        cy="19"
        r={RADIUS}
        fill="none"
        stroke="currentColor"
        strokeWidth="3"
        opacity="0.25"
      />
      <circle
        cx="19"
        cy="19"
        r={RADIUS}
        fill="none"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
        strokeDasharray={CIRCUMFERENCE}
        strokeDashoffset="0"
        className="toast-warning-ring-progress"
      />
      <path
        d="M9.68628 4.37256C9.8973 4.37256 10.1013 4.34268 10.2944 4.28692C10.3056 4.41896 10.3113 4.55256 10.3113 4.6875V7.18657L11.1977 9.16163C11.2321 9.23809 11.2498 9.32094 11.2498 9.40475C11.2498 9.73267 10.984 9.9985 10.656 9.9985H0.593897C0.510282 9.9985 0.427609 9.98084 0.351292 9.94668C0.0519959 9.81269 -0.082014 9.46144 0.051973 9.16215L0.936284 7.1868L0.936351 4.67943L0.939111 4.52323C1.02603 2.00402 3.0962 0 5.62378 0C6.52587 0 7.36844 0.254818 8.08344 0.696402C7.72064 1.08685 7.49878 1.61004 7.49878 2.18506C7.49878 3.39318 8.47816 4.37256 9.68628 4.37256ZM7.4727 10.6244C7.32355 11.5108 6.55252 12.186 5.62378 12.186C4.69504 12.186 3.92401 11.5108 3.77486 10.6244H7.4727ZM9.68628 3.74756C10.5492 3.74756 11.2488 3.048 11.2488 2.18506C11.2488 1.32211 10.5492 0.622559 9.68628 0.622559C8.82333 0.622559 8.12378 1.32211 8.12378 2.18506C8.12378 3.048 8.82333 3.74756 9.68628 3.74756Z"
        fill="var(--color-toast-warning-text)"
        transform="translate(11.5, 11) scale(1.3)"
      />
    </svg>
  )
}

export function Toaster(props: ToasterProps) {
  return (
    <Sonner
      position="top-center"
      closeButton
      className="flex items-center"
      style={{ "--width": "100%" } as React.CSSProperties}
      icons={{
        success: <SuccessRing />,
        error: <ErrorRing />,
        warning: <WarningRing />,
        info: <WarningRing />,
        close: <X className="w-[14px] h-[14px]" />,
      }}
      toastOptions={{
        duration: 3000,
        unstyled: true,
        classNames: {
          toast:
            "flex items-center justify-center gap-[10px] p-[15px] rounded-full border text-[14px] font-semibold leading-[20px] whitespace-nowrap bg-toast-warning-bg border-toast-warning-border text-toast-warning-text",
          success: "!bg-toast-success-bg !border-toast-success-border !text-toast-success-text",
          error: "!bg-toast-error-bg !border-toast-error-border !text-toast-error-text",
          warning: "!bg-toast-warning-bg !border-toast-warning-border !text-toast-warning-text",
          info: "!bg-toast-warning-bg !border-toast-warning-border !text-toast-warning-text",
          closeButton: "shrink-0",
          icon: "shrink-0",
          content: "flex-1 text-center",
          title: "font-semibold text-center",
        },
      }}
      {...props}
    />
  )
}
